/**
 * Une ligne du panier (design 4.3, maquette vente.html) : nom, detail (prix
 * unitaire ou au kilo, taxe, prix modifie, rabais en vert), quantite avec
 * moins et plus, montant de la ligne (hors taxe, apres rabais) et poubelle.
 */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { MODES_LIGNE, PORTEES_RABAIS, TYPES_RABAIS, formatMontant } from './contrat';
import { palette } from '../app/theme';

const PETIT_BOUTON = {
    minWidth: 48, width: 48, height: 48, minHeight: 48, p: 0, fontSize: 24, fontWeight: 800, borderRadius: 2.5,
};

const TAXE = { NONE: 'non taxable', TPS: 'TPS seulement', TPS_TVQ: null };

function pourcent(pb) {
    return `${String(pb / 100).replace('.', ',')} %`;
}

/** « Rabais 10 % (Marie) », « Rabais sur la vente 5,00 $ (Marie, autorisé par Paul) ». */
export function texteRabais(d) {
    if (!d) return null;
    const valeur = d.type === TYPES_RABAIS.POURCENT ? pourcent(d.valeur) : formatMontant(d.valeur);
    const qui = d.autorise_par ? `${d.par.display_name}, autorisé par ${d.autorise_par.display_name}` : d.par.display_name;
    const libelle = d.portee === PORTEES_RABAIS.VENTE ? 'Rabais sur la vente' : 'Rabais';
    return `${libelle} ${valeur} (${qui}) : -${formatMontant(d.montant_cents)}`;
}

/** Detail sous le nom. */
export function detailLigne(ligne, unite) {
    const parties = [];
    if (ligne.mode === MODES_LIGNE.POIDS) {
        const auKg = ligne.unit_price_cents;
        const grammes = auKg > 0 ? Math.round((ligne.gross_cents * 1000) / auKg) : null;
        const kg = grammes === null ? null : (grammes / 1000).toFixed(3).replace('.', ',');
        parties.push(kg ? `≈ ${kg} ${unite} × ${formatMontant(auKg)}/${unite}` : `Au poids · ${formatMontant(ligne.gross_cents)}`);
    } else {
        parties.push(`${formatMontant(ligne.unit_price_cents)} / unité`);
    }
    if (TAXE[ligne.tax_code]) parties.push(TAXE[ligne.tax_code]);
    return parties.join(' · ');
}

export default function LignePanier({
    ligne, unite = 'kg', surlignee, selectionnee, onSelect, onMoins, onPlus, onQuantite, onPrix, onSupprimer,
}) {
    const unitaire = ligne.mode === MODES_LIGNE.UNITE;
    return (
        <Box
            data-testid={`ligne-${ligne.line_no}`}
            data-productcode={ligne.productcode}
            data-qte={ligne.qty}
            data-selectionnee={selectionnee ? 'oui' : 'non'}
            onClick={onSelect}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.75, px: 2.5, minHeight: 72, py: 1, cursor: 'pointer',
                borderBottom: '1px solid #F1F5F9', fontSize: 20,
                bgcolor: surlignee ? '#EFF6FF' : 'transparent',
                boxShadow: selectionnee ? `inset 4px 0 0 ${palette.primary}` : 'none',
                transition: 'background-color 300ms',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 20, color: palette.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ligne.name}
                </Typography>
                <Typography component="div" sx={{ fontSize: 14, color: palette.textSecondary, mt: 0.25 }}>
                    {detailLigne(ligne, unite)}
                    {ligne.override && (
                        <Box component="span" data-testid="prix-modifie" sx={{ display: 'block', color: palette.warningOnTint, fontWeight: 600 }}>
                            Prix modifié ({ligne.override.par.display_name}) · catalogue {formatMontant(ligne.override.prix_catalogue_cents)}
                        </Box>
                    )}
                    {ligne.discount && (
                        <Box component="span" data-testid="rabais-ligne" sx={{ display: 'block', color: palette.successOnTint, fontWeight: 600 }}>
                            {texteRabais(ligne.discount)}
                        </Box>
                    )}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} onClick={(e) => e.stopPropagation()}>
                {unitaire && (
                    <Button variant="outlined" aria-label={`Moins un ${ligne.name}`} disabled={ligne.qty <= 1} onClick={onMoins} sx={PETIT_BOUTON}>−</Button>
                )}
                {unitaire ? (
                    <Button
                        variant="text"
                        aria-label={`Quantité de ${ligne.name} : ${ligne.qty}`}
                        data-testid="quantite"
                        onClick={onQuantite}
                        sx={{ ...PETIT_BOUTON, width: 56, color: palette.text }}
                    >
                        {ligne.qty}
                    </Button>
                ) : (
                    <Typography data-testid="quantite" sx={{ width: 56, textAlign: 'center', fontWeight: 800, fontSize: 22 }}>1</Typography>
                )}
                {unitaire && (
                    <Button variant="outlined" aria-label={`Plus un ${ligne.name}`} onClick={onPlus} sx={PETIT_BOUTON}>+</Button>
                )}
            </Box>
            <Box onClick={(e) => e.stopPropagation()} sx={{ width: 130, textAlign: 'right' }}>
                {unitaire ? (
                    <Button
                        variant="text"
                        aria-label={`Modifier le prix de ${ligne.name}`}
                        data-testid="montant-ligne"
                        onClick={onPrix}
                        sx={{ minHeight: 48, fontSize: 20, fontWeight: 700, color: palette.text, px: 1, justifyContent: 'flex-end', width: '100%' }}
                    >
                        {formatMontant(ligne.net_cents)}
                    </Button>
                ) : (
                    <Typography data-testid="montant-ligne" sx={{ fontSize: 20, fontWeight: 700, px: 1 }}>{formatMontant(ligne.net_cents)}</Typography>
                )}
            </Box>
            <IconButton
                aria-label={`Retirer ${ligne.name}`}
                onClick={(e) => { e.stopPropagation(); onSupprimer(); }}
                sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: palette.errorTint, color: palette.errorOnTint, '&:hover': { bgcolor: '#FECACA' } }}
            >
                <CloseIcon />
            </IconButton>
        </Box>
    );
}
